/**
 * Metric labels + tooltips for the portal dashboards.
 *
 * GENERATED FROM Kiosk_Backend/docs/PORTAL_DASHBOARD_METRICS.md — the metric
 * matrix is canonical; when it changes, update this dictionary in the same
 * change. Keys match the backend kpi keys (api/PORTAL_DASHBOARD_API.md).
 */
export const METRIC_INFO = {
    // -------------------------------------------------------- shared / tier
    creditsSold: {
        label: 'Credits sold',
        tooltip: 'Credits you delivered to your sub-units in the period, across cash sales, gateway purchases and balance conversions YOU funded. Sales funded by your parent or the platform are not counted (they are not your sale or margin).',
        goodWhen: 'up',
    },
    creditsSoldCount: {
        label: 'Sales count',
        tooltip: 'Number of credit sales you funded in the period (all payment paths).',
        goodWhen: 'up',
    },
    creditsWithdrawnBack: {
        label: 'Credits withdrawn back',
        tooltip: 'Credits your sub-units returned to you in the period. Moves when a credit withdrawal in your network completes. Not subtracted from Credits sold — read them together.',
        goodWhen: 'neutral',
    },
    creditCost: {
        label: 'Credit cost',
        tooltip: 'What you paid for credits in the period: purchases from the tier above you (cash), your own gateway purchases and balance→credits conversions, net of credits you returned to it.',
        goodWhen: 'neutral',
    },
    creditMargin: {
        label: 'Credit margin',
        tooltip: 'Revenue from the credits you sold minus what those credits cost you (the cost-rate spread × volume). Only covers sales you funded.',
        goodWhen: 'up',
    },
    netCashFlow: {
        label: 'Net cash flow',
        tooltip: 'Real USD entering minus leaving your online (USD) wallet in the period. IN: gateway sale revenue, settlements, reversals. OUT: withdrawals and credits you bought with balance. Distinct from Credit margin — this is cash movement, not profit.',
        goodWhen: 'up',
    },
    usdIn: {
        label: 'USD in',
        tooltip: 'USD credited to your online wallet in the period (deposits + reversals, completed only).',
        goodWhen: 'up',
    },
    usdOut: {
        label: 'USD out',
        tooltip: 'USD leaving your online wallet in the period (withdrawals + balance→credits purchases, completed only).',
        goodWhen: 'neutral',
    },
    cashExpected: {
        label: 'Cash expected',
        tooltip: 'Physical cash owed to you for cash credit sales in the period (credits × the rate they pay). Currently recorded as fully received on creation; watch Unsettled settlements for open items.',
        goodWhen: 'up',
    },
    cashReceived: {
        label: 'Cash received',
        tooltip: 'Physical cash actually collected on your cash credit sales in the period.',
        goodWhen: 'up',
    },
    unsettledSettlementsCount: {
        label: 'Unsettled settlements',
        tooltip: 'Cash sales still pending or partially paid (live count, not period-scoped). The actionable signal for uncollected cash.',
        goodWhen: 'down',
    },
    outstandingDebt: {
        label: 'Outstanding online debt',
        tooltip: 'USD you still owe the platform for payouts advanced on your behalf (live snapshot). Falls when a settlement is approved.',
        goodWhen: 'down',
    },
    creditBalance: {
        label: 'Credit balance',
        tooltip: 'Your spendable credits right now (live snapshot, no period comparison).',
        goodWhen: 'neutral',
    },
    usdBalanceNet: {
        label: 'USD wallet balance',
        tooltip: 'Withdrawable USD in your online wallet right now (net of fees, live snapshot).',
        goodWhen: 'neutral',
    },
    // -------------------------------------------------------- network / players
    playerDeposits: {
        label: 'Player deposits',
        tooltip: 'Deposits completed by Players across your network in the period (online + counter). Internal game transfers are excluded — moving credits into a game is not a deposit.',
        goodWhen: 'up',
    },
    playerDepositsCount: {
        label: 'Deposit count',
        tooltip: 'Number of completed Player deposits (online + counter) in your network in the period.',
        goodWhen: 'up',
    },
    playerWithdrawals: {
        label: 'Player withdrawals',
        tooltip: 'Player withdrawals completed across your network in the period (online + counter). Internal game-to-wallet transfers are excluded.',
        goodWhen: 'neutral',
    },
    playerWithdrawalsCount: {
        label: 'Withdrawal count',
        tooltip: 'Number of completed Player withdrawals in your network in the period.',
        goodWhen: 'neutral',
    },
    activePlayers: {
        label: 'Active Players',
        tooltip: 'Unique Players with payment activity in the period.',
        goodWhen: 'up',
    },
    newPlayers: {
        label: 'New Players',
        tooltip: 'Player registrations in the period.',
        goodWhen: 'up',
    },
    // -------------------------------------------------------- shop overview
    depositsOnline: {
        label: 'Online deposits',
        tooltip: 'Gateway deposits completed by your Players in the period (LinkMePay, BTCPay, 0x, Meld). Moves your USD wallet by the net amount. Internal game transfers excluded.',
        goodWhen: 'up',
    },
    depositsOnlineCount: {
        label: 'Online deposit count',
        tooltip: 'Number of completed online Player deposits in the period.',
        goodWhen: 'up',
    },
    depositsCounter: {
        label: 'Counter deposits',
        tooltip: 'Cash taken at the counter for credits in the period (recorded by your Cashiers). Never overlaps with Online deposits — different ledger rows by construction.',
        goodWhen: 'up',
    },
    depositsCounterCount: {
        label: 'Counter deposit count',
        tooltip: 'Number of counter deposits recorded in the period.',
        goodWhen: 'up',
    },
    redemptionsOnline: {
        label: 'Online withdrawals',
        tooltip: 'Player online withdrawals completed in the period.',
        goodWhen: 'neutral',
    },
    redemptionsOnlineCount: {
        label: 'Online withdrawal count',
        tooltip: 'Number of completed online Player withdrawals in the period.',
        goodWhen: 'neutral',
    },
    redemptionsCounter: {
        label: 'Counter redemptions',
        tooltip: 'Cash paid out at the counter for Player redemptions in the period.',
        goodWhen: 'neutral',
    },
    redemptionsCounterCount: {
        label: 'Counter redemption count',
        tooltip: 'Number of counter redemptions paid in the period.',
        goodWhen: 'neutral',
    },
    creditsIssued: {
        label: 'Credits issued',
        tooltip: 'Credits delivered to your Players in the period (counter + online deposits), net of counter redemption returns.',
        goodWhen: 'up',
    },
    creditReserved: {
        label: 'Reserved credits',
        tooltip: 'Credits currently reserved for pending payouts (live snapshot).',
        goodWhen: 'neutral',
    },
    creditAvailable: {
        label: 'Available credits',
        tooltip: 'Credit balance minus reservations — what you can actually spend right now.',
        goodWhen: 'neutral',
    },
    avgDeposit: {
        label: 'Average deposit',
        tooltip: 'Player deposits divided by deposit count in the period (online + counter).',
        goodWhen: 'up',
    },
    firstDepositConversionPct: {
        label: 'First-deposit conversion',
        tooltip: 'Share of Players who registered in the period and also made their first deposit in it.',
        goodWhen: 'up',
    },
    playerCreditLiability: {
        label: 'Player credit liability',
        tooltip: 'Sum of your Players’ wallet balances right now — credits you owe your Players (live snapshot).',
        goodWhen: 'neutral',
    },
    // -------------------------------------------------------- shop operations
    openShifts: {
        label: 'Open shifts',
        tooltip: 'Cashier shifts active right now (live snapshot).',
        goodWhen: 'neutral',
    },
    discrepancyShifts: {
        label: 'Discrepancy shifts',
        tooltip: 'Shifts closed with a cash-drawer discrepancy in the period (by close time). Where Shops lose money silently — investigate each one.',
        goodWhen: 'down',
    },
    discrepancyAmount: {
        label: 'Discrepancy total',
        tooltip: 'Total cash discrepancy across shifts closed in the period.',
        goodWhen: 'down',
    },
    noShowShifts: {
        label: 'No-show shifts',
        tooltip: 'Scheduled shifts the Cashier missed in the period.',
        goodWhen: 'down',
    },
    pendingRedemptionApprovals: {
        label: 'Pending approvals',
        tooltip: 'Counter redemptions awaiting manager approval right now (live snapshot).',
        goodWhen: 'down',
    },
    cashierTransactionsCount: {
        label: 'Cashier transactions',
        tooltip: 'Completed counter transactions in the period (deposits, redemptions, withdrawals).',
        goodWhen: 'up',
    },
    cashierCashDelta: {
        label: 'Counter cash delta',
        tooltip: 'Net cash movement across Cashier drawers in the period (in minus out).',
        goodWhen: 'up',
    },
    cashDropsTotal: {
        label: 'Cash drops',
        tooltip: 'Cash moved from drawers to the safe in the period.',
        goodWhen: 'neutral',
    },
    cashDropsPendingCount: {
        label: 'Unconfirmed drops',
        tooltip: 'Cash drops recorded but not yet confirmed (live snapshot).',
        goodWhen: 'down',
    },
};
/** KPI keys per portal tab — completeness-tested so every KPI has a tooltip. */
export const TIER_OVERVIEW_KPI_KEYS = [
    'creditsSold',
    'creditsSoldCount',
    'creditsWithdrawnBack',
    'creditCost',
    'creditMargin',
    'netCashFlow',
    'usdIn',
    'usdOut',
    'cashExpected',
    'cashReceived',
    'unsettledSettlementsCount',
    'outstandingDebt',
    'creditBalance',
    'usdBalanceNet',
];
export const NETWORK_KPI_KEYS = [
    'playerDeposits',
    'playerDepositsCount',
    'playerWithdrawals',
    'playerWithdrawalsCount',
    'activePlayers',
    'newPlayers',
];
export const SHOP_OVERVIEW_KPI_KEYS = [
    'depositsOnline',
    'depositsOnlineCount',
    'depositsCounter',
    'depositsCounterCount',
    'redemptionsOnline',
    'redemptionsOnlineCount',
    'redemptionsCounter',
    'redemptionsCounterCount',
    'netCashFlow',
    'usdIn',
    'usdOut',
    'creditMargin',
    'creditsIssued',
    'creditCost',
    'creditBalance',
    'creditReserved',
    'creditAvailable',
    'usdBalanceNet',
];
export const SHOP_PLAYERS_KPI_KEYS = [
    'activePlayers',
    'newPlayers',
    'avgDeposit',
    'firstDepositConversionPct',
    'playerCreditLiability',
];
export const SHOP_OPERATIONS_KPI_KEYS = [
    'openShifts',
    'discrepancyShifts',
    'discrepancyAmount',
    'noShowShifts',
    'pendingRedemptionApprovals',
    'cashierTransactionsCount',
    'cashierCashDelta',
    'cashDropsTotal',
    'cashDropsPendingCount',
];
export function metricInfo(key) {
    return (METRIC_INFO[key] || {
        label: key,
        tooltip: '',
        goodWhen: 'neutral',
    });
}
